<template>
  <Field
    v-slot="{ errors, field, validate, meta }"
    v-model="passwordValue"
    :name="name"
    :rules="validationTypedSchema"
    :validate-on-mount="false"
    :validate-on-input="false"
    :validate-on-blur="false"
  >
    <label
      :class="{
        error: hasError?.(errors) || false,
        disabled: disabled,
        required: isRequired,
      }"
    >
      <slot name="label" />
      <slot name="label-status">
        <span v-if="isRequired" class="label-status">*</span>
        <span v-else class="label-help">({{ $t('optional') }})</span>
      </slot>
      <input
        :type="passwordFieldType"
        :name="name"
        v-bind="field"
        :disabled="disabled"
        :required="isRequired"
        :minlength="$attrs['minlength']"
        autocapitalize="off"
        :autocomplete="$attrs['autocomplete'] || 'current-password'"
        autocorrect="off"
        spellcheck="false"
        @input="handleInput($event, validate, meta)"
      />
    </label>
    <input-error-message
      v-if="!hideIndividualErrors"
      :has-error="hasError?.(errors) || false"
    >
      <slot
        v-if="Object.keys(errors).length"
        name="errorMessage"
        :failed-rules="errors"
      >
        <translate v-if="errors.includes('required')">
          Please enter your password
        </translate>

        <translate
          v-else-if="errors.includes('min')"
          :tt="{ min: $attrs.minlength, passwordLength: passwordValue.length }"
        >
          Ensure your password has at least {min} characters (it has
          {passwordLength})
        </translate>

        <translate
          v-else-if="errors.includes('max')"
          :tt="{ max: $attrs.maxlength }"
        >
          Please shorten this text to {max} characters or fewer
        </translate>

        <translate v-else-if="validationErrors?.length">
          {{ validationErrors[0] }}
        </translate>
        <div v-else>
          {{ errors[0] }}
        </div>
      </slot>
    </input-error-message>
    <p v-if="$slots.helpText" class="help-text">
      <slot name="helpText" />
    </p>
    <div class="show-password">
      <base-checkbox v-model="showPassword">
        {{ $t('Show password') }}
      </base-checkbox>
    </div>
  </Field>
</template>

<script lang="ts">
import { Component, Vue, Watch, toNative } from '~/utility/pu-class-decorator'
import { Field } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import BaseInput from './BaseInput.vue'

@Component({
  mixins: [BaseInput],
  emits: ['update:modelValue', 'input', 'update:model-value'],
  components: { Field },
})
class BasePassword extends Vue {
  showPassword = false
  passwordValue: string = ''

  mounted(): void {
    this.passwordValue = this.modelValue as string
  }

  @Watch('modelValue')
  onModelValueChanged(newValue: string) {
    this.passwordValue = newValue || ''
  }

  get passwordFieldType(): string {
    return this.showPassword ? 'text' : 'password'
  }

  get validationTypedSchema() {
    return toTypedSchema(this.validationRules)
  }

  handleInput(event: Event, validate: () => void, meta: { touched: boolean }) {
    const target = event.target as HTMLInputElement
    this.passwordValue = target.value
    this.$emit('update:modelValue', target.value)
    this.$emit('input', target.value)

    // Only validate on input if field has content - prevents validation errors
    // on empty fields from password manager blur events
    if (meta.touched && target.value.length > 0) {
      validate()
    }
  }
}

export default toNative(BasePassword)
</script>

<style scoped>
.show-password {
  margin: 0.5em 0 0 0;
  display: block;
}

.show-password :deep(label) {
  font-weight: normal;
  font-size: var(--small);
}
</style>
